import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaStar } from "react-icons/fa";
import { FaInfoCircle } from "react-icons/fa";
import Layout from '../../Components/Layout';
import config from '../../config';
import Swal from 'sweetalert2'
import { TiTick } from "react-icons/ti";
import { getPrice } from '../../services/priceUtils';
import { useCurrency } from '../../context/CurrencyContext';
import Select from "react-dropdown-select";

export default function Checkout() {
    const [rate, setRate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [totalPrice, setTotalPrice] = useState("0.00");
    const [cancellationPrice, setCancellationPrice] = useState("0.00");
    const [balance, setBalance] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const userData = JSON.parse(localStorage.getItem('user_data'));
    const [formData, setFormData] = useState({});
    const { selectedCurrency } = useCurrency();
    const [hotelData, setHotelData] = useState(null);
    const [countries, setCountries] = useState([]);
    const [selectedCountryCode, setSelectedCountryCode] = useState('+90');
    const [cartItems, setCartItems] = useState([]);
    const [convertedPrices, setConvertedPrices] = useState({});
    const [cancellationPrices, setCancellationPrices] = useState({});

    // URL'den parametreleri al ve memoize et
    const params = useMemo(() => ({
        checkIn: searchParams.get('checkIn'),
        checkOut: searchParams.get('checkOut'),
        rooms: searchParams.get('rooms')?.split(',') || ['1'],
        adults: searchParams.get('adults')?.split(',') || ['2'],
        children: searchParams.get('children')?.split(',') || ['0'],
        ages: searchParams.get('ages')?.split('|').map(age => age.split('-')) || [[]],
        nationality: searchParams.get('nationality') || 'TR'
    }), [searchParams]);

    // Memoize calculateDays function
    const calculateDays = useCallback((checkIn, checkOut) => {
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }, []);

    // Memoize getCheckoutPrice function
    const getCheckoutPrice = useCallback((room, hotelData) => {
        if (!room?.rates?.[0]?.taxes?.length) return Number(totalPrice);
        
        let taxedPrice = Number(totalPrice);
        const taxes = Array.isArray(room.rates[0].taxes) ? room.rates[0].taxes : room.rates[0].taxes?.taxes || [];
        
        taxes.forEach(tax => {
            if(tax.included === false) {
                taxedPrice += Number(tax.amount);
            }
        });
        
        return taxedPrice;
    }, [totalPrice]);

    // Memoize calculatePrices function
    const calculatePrices = useCallback(async (hotelData) => {
        if (!hotelData?.rooms?.[0]?.rates?.[0]?.net) return;

        try {
            const rate = hotelData.rooms[0].rates[0];
            const newConvertedPrices = {};
            const newCancellationPrices = {};
            
            // Eğer para birimi EUR ise direkt net değeri kullan
            if (rate.currency === 'EUR') {
                newConvertedPrices[rate.offerKey] = rate.net;
                if (rate.cancellationPolicies?.[0]?.amount) {
                    newCancellationPrices[rate.offerKey] = {
                        [rate.cancellationPolicies[0].from]: rate.cancellationPolicies[0].amount
                    };
                }
            } else {
                // EUR değilse getPrice ile dönüşüm yap
                const [totalPrice, cancellationPrice] = await Promise.all([
                    getPrice(
                        rate.net,
                        userData.token,
                        selectedCurrency,
                        rate.taxes,
                        hotelData.hotel.country?.code,
                        rate.currency
                    ),
                    rate.cancellationPolicies?.[0]?.amount ?
                        getPrice(
                            rate.cancellationPolicies[0].amount,
                            userData.token,
                            selectedCurrency,
                            null,
                            null,
                            rate.currency
                        ) : "0.00"
                ]);

                newConvertedPrices[rate.offerKey] = totalPrice || "0.00";
                
                // İptal fiyatlarını dönüştür
                if (rate.cancellationPolicies?.length > 0) {
                    for (const policy of rate.cancellationPolicies) {
                        if (policy.amount) {
                            const convertedCancellationPrice = await getPrice(
                                policy.amount,
                                userData.token,
                                selectedCurrency,
                                null,
                                null,
                                rate.currency
                            );
                            if (!newCancellationPrices[rate.offerKey]) {
                                newCancellationPrices[rate.offerKey] = {};
                            }
                            newCancellationPrices[rate.offerKey][policy.from] = convertedCancellationPrice;
                        }
                    }
                }
            }

            setConvertedPrices(newConvertedPrices);
            setCancellationPrices(newCancellationPrices);
            setTotalPrice(newConvertedPrices[rate.offerKey] || "0.00");
            setCancellationPrice(newCancellationPrices[rate.offerKey]?.[rate.cancellationPolicies?.[0]?.from] || "0.00");
        } catch (error) {
            console.error('Error calculating prices:', error);
            setTotalPrice("0.00");
            setCancellationPrice("0.00");
            setConvertedPrices({});
            setCancellationPrices({});
        }
    }, [userData.token, selectedCurrency]);

    // Memoize handleChange function
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Memoize checkBalance function
    const checkBalance = useCallback(async (requiredAmount) => {
        try {
            const balanceResponse = await axios.get(`${config.API_BASE_URL_V1}wallet/balance`, {
                headers: {
                    Authorization: `Bearer ${userData.token}`,
                },
            });

            const currentBalance = Number(balanceResponse.data.data.balance || 0);
            setBalance(currentBalance);

            if (currentBalance < requiredAmount) {
                Swal.fire({
                    title: "Insufficient Balance",
                    text: `Required amount: ${requiredAmount}, Your balance: ${currentBalance}`,
                    icon: "error",
                    draggable: true,
                });
                navigate('/dashboard-credit');
                return false;
            }
            return true;
        } catch (error) {
            console.error("Error checking balance:", error);
            Swal.fire({
                title: "Balance Check Error",
                text: "Could not verify your balance. Please try again.",
                icon: "error",
                draggable: true,
            });
            return false;
        }
    }, [userData.token, navigate]);

    // Main useEffect with optimized dependencies
    useEffect(() => {
        const fetchCart = async () => {
            try {
                setLoading(true);
                
                // Sepet verilerini çek
                const response = await axios.get(`${config.API_BASE_URL_V1}basket`, {
                    headers: {
                        Authorization: `Bearer ${userData.token}`,
                    }
                });

                if (response.data.status === 'success' && response.data.data?.basket?.basketItems?.length > 0) {
                    const basketItems = response.data.data.basket.basketItems;
                    setCartItems(basketItems);
                    
                    // Toplam fiyatı hesapla
                    const total = basketItems.reduce((sum, item) => {
                        return sum + Number(item.price);
                    }, 0);
                    setTotalPrice(total);
                }
            } catch (error) {
                console.error('Error fetching cart:', error);
                Swal.fire({
                    title: "Hata!",
                    text: error.response?.data?.message || "Sepet verileri alınamadı",
                    icon: "error",
                    draggable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchCart();
    }, [userData.token, selectedCurrency]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await axios.get(`${config.API_BASE_URL}geo/countries`);
                setCountries(response.data);
            } catch (err) {
                console.error('Error fetching countries:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch countries',
                });
            }
        };
        fetchCountries();
    }, []);

    const handlePhoneChange = (e, index) => {
        const { name, value } = e.target;
        // Sadece rakamları al
        const numericValue = value.replace(/\D/g, '');
        setFormData(prev => ({ ...prev, [name]: numericValue }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setButtonLoading(true);
            const requiredAmount = cartItems.reduce((sum, item) => sum + Number(item.price), 0);
            const hasBalance = await checkBalance(requiredAmount);
            if (!hasBalance) {
                setButtonLoading(false);
                return;
            }

            // Get the first adult's information as holder from any item type
            let firstAdult = null;
            let holderItemIndex = 0;
            let holderAdultIndex = 0;

            // Find first adult from hotels, tours, or transfers
            for (let itemIndex = 0; itemIndex < cartItems.length; itemIndex++) {
                const item = cartItems[itemIndex];
                let adultsCount = 0;

                if (item.type === 'hotel') {
                    adultsCount = item.room.rates[0].adults;
                } else if (item.type === 'tour') {
                    adultsCount = item.booking_details.adults;
                } else if (item.type === 'transfer') {
                    adultsCount = item.passengers.adults;
                }

                if (adultsCount > 0) {
                    firstAdult = {
                        name: formData[`firstName_adult_${itemIndex}_0`]?.trim(),
                        surname: formData[`lastName_adult_${itemIndex}_0`]?.trim(),
                        email: formData[`email_adult_${itemIndex}_0`]?.trim()
                    };
                    holderItemIndex = itemIndex;
                    break;
                }
            }

            if (!firstAdult?.name || !firstAdult?.surname) {
                throw new Error("First adult information is required");
            }

            // Separate items by type
            const hotels = cartItems.filter(item => item.type !== 'tour' && item.type !== 'transfer');
            const tours = cartItems.filter(item => item.type === 'tour');
            const transfers = cartItems.filter(item => item.type === 'transfer');

            const requestData = {
                holder: firstAdult
            };

            // Add hotels if any
            if (hotels.length > 0) {
                requestData.rooms = hotels.map((item, roomIndex) => {
                    const actualIndex = cartItems.findIndex(cartItem => cartItem === item);
                    return {
                        offerKey: item.offer_key,
                        paxes: [
                            // Add adults
                            ...Array(item.room.rates[0].adults).fill().map((_, adultIndex) => ({
                                type: "AD",
                                name: formData[`firstName_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                surname: formData[`lastName_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                email: formData[`email_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                phone: formData[`phone_adult_${actualIndex}_${adultIndex}`] ? 
                                    `${selectedCountryCode}${formData[`phone_adult_${actualIndex}_${adultIndex}`]}` : undefined,
                                passport: formData[`passport_adult_${actualIndex}_${adultIndex}`]?.trim()
                            })),
                            // Add children
                            ...Array(item.room.rates[0].children).fill().map((_, childIndex) => ({
                                type: "CH",
                                name: formData[`firstName_child_${actualIndex}_${childIndex}`]?.trim(),
                                surname: formData[`lastName_child_${actualIndex}_${childIndex}`]?.trim(),
                                age: item.room.rates[0].childrenAges?.split(',')?.[childIndex] || "0",
                                passport: formData[`passport_child_${actualIndex}_${childIndex}`]?.trim()
                            }))
                        ].filter(pax => pax.name && pax.surname) // Remove empty entries
                    };
                });
            }

            // Add tours if any
            if (tours.length > 0) {
                requestData.tours = tours.map((item, tourIndex) => {
                    const actualIndex = cartItems.findIndex(cartItem => cartItem === item);
                    return {
                        offerKey: item.offer_key,
                        participants: [
                            // Add adults
                            ...Array(item.booking_details.adults).fill().map((_, adultIndex) => ({
                                type: "AD",
                                name: formData[`firstName_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                surname: formData[`lastName_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                email: formData[`email_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                phone: formData[`phone_adult_${actualIndex}_${adultIndex}`] ? 
                                    `${selectedCountryCode}${formData[`phone_adult_${actualIndex}_${adultIndex}`]}` : undefined,
                                passport: formData[`passport_adult_${actualIndex}_${adultIndex}`]?.trim()
                            })),
                            // Add children
                            ...Array(item.booking_details.children).fill().map((_, childIndex) => ({
                                type: "CH",
                                name: formData[`firstName_child_${actualIndex}_${childIndex}`]?.trim(),
                                surname: formData[`lastName_child_${actualIndex}_${childIndex}`]?.trim(),
                                age: item.booking_details.children_ages?.[childIndex] || "0",
                                passport: formData[`passport_child_${actualIndex}_${childIndex}`]?.trim()
                            }))
                        ].filter(participant => participant.name && participant.surname) // Remove empty entries
                    };
                });
            }

            // Add transfers if any
            if (transfers.length > 0) {
                requestData.transfers = transfers.map((item, transferIndex) => {
                    const actualIndex = cartItems.findIndex(cartItem => cartItem === item);
                    const transferData = {
                        offerKey: item.offer_key,
                        passengers: [
                            // Add adults
                            ...Array(item.passengers.adults).fill().map((_, adultIndex) => ({
                                type: "AD",
                                name: formData[`firstName_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                surname: formData[`lastName_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                email: formData[`email_adult_${actualIndex}_${adultIndex}`]?.trim(),
                                phone: formData[`phone_adult_${actualIndex}_${adultIndex}`] ? 
                                    `${selectedCountryCode}${formData[`phone_adult_${actualIndex}_${adultIndex}`]}` : undefined,
                                passport: formData[`passport_adult_${actualIndex}_${adultIndex}`]?.trim()
                            })),
                            // Add children
                            ...Array(item.passengers.children).fill().map((_, childIndex) => ({
                                type: "CH",
                                name: formData[`firstName_child_${actualIndex}_${childIndex}`]?.trim(),
                                surname: formData[`lastName_child_${actualIndex}_${childIndex}`]?.trim(),
                                age: item.passengers.children_ages?.[childIndex] || "0",
                                passport: formData[`passport_child_${actualIndex}_${childIndex}`]?.trim()
                            }))
                        ].filter(passenger => passenger.name && passenger.surname), // Remove empty entries
                        flight_info: {}
                    };

                    // Add flight info based on transfer type
                    if (item.flight_number && item.flight_time) {
                        transferData.flight_info.departure = {
                            flight_number: item.flight_number,
                            flight_time: item.flight_time
                        };
                    }

                    // Add return flight info if round trip
                    if (item.is_round_trip && item.departure_flight_number && item.departure_flight_time) {
                        transferData.flight_info.return = {
                            flight_number: item.departure_flight_number,
                            flight_time: item.departure_flight_time
                        };
                    }

                    return transferData;
                });
            }

            // Add notes if provided
            if (formData.notes?.trim()) {
                requestData.notes = formData.notes.trim();
            }

            console.log('Booking request data:', requestData);

            const apiUrl = `${config.API_BASE_URL_V1}hotel/booking-confirm`;
            const response = await axios.post(apiUrl, requestData, {
                headers: {
                    Authorization: `Bearer ${userData.token}`,
                    "Content-Type": "application/json",
                },
            });

            if(response.data.status === 'success') {
                const orderData = response.data.data;
                const successData = {
                    id: orderData.id
                };
                navigate('/payment-successful', {state: successData});
                window.scrollTo(0, 0);
            } else {
                throw new Error(response.data.message || "Booking failed");
            }
        } catch (error) {
            console.error("Error:", error);
            Swal.fire({
                title: "Booking Error",
                text: error.message || "An error occurred during booking",
                icon: "error",
                draggable: true,
            });
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setButtonLoading(false);
        }
    };

    console.log('hotelData: ', hotelData);

    if(loading) {
        return (
            <Layout>
                <div className="content content-two">
                    <div className="container">
                        <div className="row justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <div className="col-md-12 text-center">
                                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h5 className="mt-3" style={{ color: '#1e88e5' }}>Loading Checkout Details...</h5>
                                <p className="text-muted">Please wait while we prepare your booking information.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    const formatCancellationDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const getCurrencySymbol = (currency) => {
        const symbols = {
            'USD': '$',
            'EUR': '€',
            'GBP': '£',
            'TRY': '₺'
        };
        return symbols[currency] || currency;
    };

    return (
        <Layout>
            <div className="breadcrumb-bar breadcrumb-bg-01 text-center">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2"> {hotelData?.hotel?.name} </h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center mb-0">
                                    <li className="breadcrumb-item"><a ><i className="isax isax-home5"></i></a></li>
                                    <li className="breadcrumb-item">Hotels</li>
                                    <li className="breadcrumb-item active" aria-current="page"> Hotel Booking </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content content-two">
                <div className="container">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="alert alert-info mb-4" style={{
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                    <div className="d-flex align-items-center">
                                        <i className="isax isax-info-circle" style={{ fontSize: '24px', marginRight: '12px', color: '#1e88e5' }}></i>
                                        <div>
                                            <h6 style={{ color: '#1e88e5', marginBottom: '4px' }}>Required Information</h6>
                                            <p style={{ color: '#424242', margin: 0, fontSize: '0.95rem' }}>
                                                To proceed with your booking, please fill in all required fields marked with an asterisk (*) for each guest.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {cartItems.map((item, itemIndex) => {
                                    // Determine item type and get appropriate data
                                    let itemTitle = '';
                                    let adultsCount = 0;
                                    let childrenCount = 0;
                                    let itemColor = '#1e88e5';

                                    if (item.type === 'hotel') {
                                        itemTitle = `${item.room.name} (${item.room.rates[0].boardName}) - ${item.quantity} ${item.quantity > 1 ? 'Rooms' : 'Room'}`;
                                        adultsCount = item.room.rates[0].adults;
                                        childrenCount = item.room.rates[0].children;
                                        itemColor = '#1e88e5';
                                    } else if (item.type === 'tour') {
                                        itemTitle = `${item.tour.name} - ${item.quantity} ${item.quantity > 1 ? 'Tours' : 'Tour'}`;
                                        adultsCount = item.booking_details.adults;
                                        childrenCount = item.booking_details.children;
                                        itemColor = 'rgba(210, 110, 50, 0.95)';
                                    } else if (item.type === 'transfer') {
                                        itemTitle = `${item.transfer.vehicle.type} ${item.transfer.vehicle.category} Transfer - ${item.is_round_trip ? 'Round Trip' : 'One Way'}`;
                                        adultsCount = item.passengers.adults;
                                        childrenCount = item.passengers.children;
                                        itemColor = 'rgba(210, 110, 50, 0.95)';
                                    }

                                    return (
                                        <div key={itemIndex} className="card checkout-card mb-4" style={{
                                            borderRadius: '15px',
                                            border: 'none',
                                            boxShadow: '0 0 20px rgba(0,0,0,0.05)',
                                            transition: 'transform 0.3s ease',
                                            background: '#fff'
                                        }}>
                                            <div className="card-header" style={{
                                                background: '#fff',
                                                borderBottom: '2px solid #f5f5f5',
                                                padding: '1.5rem',
                                                borderTopLeftRadius: '15px',
                                                borderTopRightRadius: '15px'
                                            }}>
                                                <h5 style={{ 
                                                    color: itemColor,
                                                    fontSize: '1.5rem',
                                                    fontWeight: '600',
                                                    margin: 0
                                                }}>
                                                    {itemTitle}
                                                </h5>
                                            </div>
                                        <div className="card-body" style={{ padding: '2rem' }}>
                                            {/* Yetişkin Bilgileri */}
                                            <div className="adults-info mb-4">
                                                <h6 className="section-title mb-3" style={{
                                                    color: itemColor,
                                                    fontSize: '1.2rem',
                                                    fontWeight: '500',
                                                    borderLeft: `4px solid ${itemColor}`,
                                                    paddingLeft: '10px'
                                                }}>
                                                    Adult Information ({adultsCount * (item.quantity || 1)} {adultsCount * (item.quantity || 1) > 1 ? 'Adults' : 'Adult'})
                                                </h6>
                                                {[...Array(adultsCount * (item.quantity || 1))].map((_, index) => (
                                                    <div
                                                        key={`adult_${itemIndex}_${index}`}
                                                        style={{
                                                            border: 'none',
                                                            borderRadius: '12px',
                                                            background: '#f8f9fa',
                                                            padding: '20px',
                                                            marginBottom: '20px',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    >
                                                        <div className="row">
                                                            <div className="col-lg-6">
                                                                <div className="mb-3">
                                                                    <label className="form-label" style={{
                                                                        color: '#424242',
                                                                        fontWeight: '500',
                                                                        marginBottom: '8px'
                                                                    }}>First Name<span style={{ color: 'red', cursor: 'help' }} title="Required">*</span></label>
                                                                    <input
                                                                        type="text"
                                                                        name={`firstName_adult_${itemIndex}_${index}`}
                                                                        className="form-control"
                                                                        style={{
                                                                            borderRadius: '8px',
                                                                            border: '1px solid #e0e0e0',
                                                                            padding: '12px',
                                                                            transition: 'border-color 0.3s ease'
                                                                        }}
                                                                        value={formData[`firstName_adult_${itemIndex}_${index}`] || ''}
                                                                        onChange={handleChange}
                                                                        required
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-6">
                                                                <div className="mb-3">
                                                                    <label className="form-label" style={{
                                                                        color: '#424242',
                                                                        fontWeight: '500',
                                                                        marginBottom: '8px'
                                                                    }}>Last Name<span style={{ color: 'red', cursor: 'help' }} title="Required">*</span></label>
                                                                    <input
                                                                        type="text"
                                                                        name={`lastName_adult_${itemIndex}_${index}`}
                                                                        className="form-control"
                                                                        style={{
                                                                            borderRadius: '8px',
                                                                            border: '1px solid #e0e0e0',
                                                                            padding: '12px',
                                                                            transition: 'border-color 0.3s ease'
                                                                        }}
                                                                        value={formData[`lastName_adult_${itemIndex}_${index}`] || ''}
                                                                        onChange={handleChange}
                                                                        required
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-lg-6">
                                                                <div className="mb-3">
                                                                    <label className="form-label" style={{
                                                                        color: '#424242',
                                                                        fontWeight: '500',
                                                                        marginBottom: '8px'
                                                                    }}>Email <span className="text-muted small">(Optional)</span></label>
                                                                    <input
                                                                        type="email"
                                                                        name={`email_adult_${itemIndex}_${index}`}
                                                                        className="form-control"
                                                                        style={{
                                                                            borderRadius: '8px',
                                                                            border: '1px solid #e0e0e0',
                                                                            padding: '12px',
                                                                            transition: 'border-color 0.3s ease'
                                                                        }}
                                                                        value={formData[`email_adult_${itemIndex}_${index}`] || ''}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-6">
                                                                <div className="mb-3">
                                                                    <label className="form-label" style={{
                                                                        color: '#424242',
                                                                        fontWeight: '500',
                                                                        marginBottom: '8px'
                                                                    }}>Phone <span className="text-muted small">(Optional)</span></label>
                                                                    <div className="input-group">
                                                                        <Select
                                                                            options={countries.map(country => ({
                                                                                value: country.phonecode,
                                                                                label: country.name,
                                                                                flag: country.iso2.toLowerCase(),
                                                                                searchValue: `${country.name} ${country.phonecode}`
                                                                            }))}
                                                                            values={[{
                                                                                value: selectedCountryCode,
                                                                                label: countries.find(c => c.phonecode === selectedCountryCode)?.name || 'Turkey',
                                                                                flag: countries.find(c => c.phonecode === selectedCountryCode)?.iso2.toLowerCase() || 'tr'
                                                                            }]}
                                                                            onChange={(values) => setSelectedCountryCode(values[0]?.value || '+90')}
                                                                            searchable={true}
                                                                            searchBy="searchValue"
                                                                            placeholder="Search country or code..."
                                                                            style={{
                                                                                width: '100%',
                                                                                border: '1px solid #e0e0e0',
                                                                                borderRadius: '8px 0 0 8px'
                                                                            }}
                                                                            contentRenderer={({ props, state, methods }) => (
                                                                                <div style={{ 
                                                                                    display: 'flex', 
                                                                                    alignItems: 'center',
                                                                                    padding: '8px',
                                                                                    gap: '8px'
                                                                                }}>
                                                                                    <img 
                                                                                        src={`https://flagcdn.com/w40/${state.values[0]?.flag || 'tr'}.png`}
                                                                                        alt={state.values[0]?.label}
                                                                                        style={{ 
                                                                                            width: '24px',
                                                                                            height: '18px',
                                                                                            objectFit: 'cover',
                                                                                            borderRadius: '2px'
                                                                                        }}
                                                                                        onContextMenu={(e) => e.preventDefault()}
                                                                                        onDragStart={(e) => e.preventDefault()}
                                                                                    />
                                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{state.values[0]?.label || 'Turkey'}</span>
                                                                                        <span style={{ fontSize: '12px', color: '#666' }}>{state.values[0]?.value || '+90'}</span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            itemRenderer={({ item, itemIndex, props, state, methods }) => (
                                                                                <div 
                                                                                    key={itemIndex}
                                                                                    onClick={() => methods.addItem(item)}
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        padding: '8px 12px',
                                                                                        cursor: 'pointer',
                                                                                        gap: '8px'
                                                                                    }}
                                                                                >
                                                                                    <img 
                                                                                        src={`https://flagcdn.com/w40/${item.flag}.png`}
                                                                                        alt={item.label}
                                                                                        style={{ 
                                                                                            width: '24px',
                                                                                            height: '18px',
                                                                                            objectFit: 'cover',
                                                                                            borderRadius: '2px'
                                                                                        }}
                                                                                        onContextMenu={(e) => e.preventDefault()}
                                                                                        onDragStart={(e) => e.preventDefault()}
                                                                                    />
                                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{item.label}</span>
                                                                                        <span style={{ fontSize: '12px', color: '#666' }}>{item.value}</span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        />
                                                                        <input
                                                                            type="text"
                                                                            name={`phone_adult_${itemIndex}_${index}`}
                                                                            className="form-control"
                                                                            style={{
                                                                                borderRadius: '0 8px 8px 0',
                                                                                border: '1px solid #e0e0e0',
                                                                                padding: '12px',
                                                                                transition: 'border-color 0.3s ease'
                                                                            }}
                                                                            value={formData[`phone_adult_${itemIndex}_${index}`] || ''}
                                                                            onChange={(e) => handlePhoneChange(e, index)}
                                                                            placeholder="Enter phone number"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-lg-12">
                                                                <div className="mb-3">
                                                                    <label className="form-label" style={{
                                                                        color: '#424242',
                                                                        fontWeight: '500',
                                                                        marginBottom: '8px'
                                                                    }}>Passport Number <span className="text-muted small">(Optional)</span></label>
                                                                    <input
                                                                        type="text"
                                                                                                                                name={`passport_adult_${itemIndex}_${index}`}
                                                        className="form-control"
                                                        style={{
                                                            borderRadius: '8px',
                                                            border: '1px solid #e0e0e0',
                                                            padding: '12px',
                                                            transition: 'border-color 0.3s ease'
                                                        }}
                                                        value={formData[`passport_adult_${itemIndex}_${index}`] || ''}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Çocuk Bilgileri */}
                                            {childrenCount > 0 && (
                                                <div className="children-info mb-4">
                                                    <h6 className="section-title mb-3" style={{
                                                        color: itemColor,
                                                        fontSize: '1.2rem',
                                                        fontWeight: '500',
                                                        borderLeft: `4px solid ${itemColor}`,
                                                        paddingLeft: '10px',
                                                        marginTop: '20px'
                                                    }}>
                                                        Children Information ({childrenCount * (item.quantity || 1)} {childrenCount * (item.quantity || 1) > 1 ? 'Children' : 'Child'})
                                                    </h6>
                                                    {[...Array(childrenCount * (item.quantity || 1))].map((_, index) => (
                                                        <div
                                                            key={`child_${itemIndex}_${index}`}
                                                            style={{
                                                                border: 'none',
                                                                borderRadius: '12px',
                                                                background: '#f8f9fa',
                                                                padding: '20px',
                                                                marginBottom: '20px',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            <div className="row">
                                                                <div className="col-lg-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label" style={{
                                                                            color: '#424242',
                                                                            fontWeight: '500',
                                                                            marginBottom: '8px'
                                                                        }}>First Name<span style={{ color: 'red', cursor: 'help' }} title="Required">*</span></label>
                                                                        <input
                                                                            type="text"
                                                                                                                                        name={`firstName_child_${itemIndex}_${index}`}
                                                            className="form-control"
                                                            style={{
                                                                borderRadius: '8px',
                                                                border: '1px solid #e0e0e0',
                                                                padding: '12px',
                                                                transition: 'border-color 0.3s ease'
                                                            }}
                                                            value={formData[`firstName_child_${itemIndex}_${index}`] || ''}
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label" style={{
                                                                            color: '#424242',
                                                                            fontWeight: '500',
                                                                            marginBottom: '8px'
                                                                        }}>Last Name<span style={{ color: 'red', cursor: 'help' }} title="Required">*</span></label>
                                                                        <input
                                                                            type="text"
                                                                                                                                        name={`lastName_child_${itemIndex}_${index}`}
                                                            className="form-control"
                                                            style={{
                                                                borderRadius: '8px',
                                                                border: '1px solid #e0e0e0',
                                                                padding: '12px',
                                                                transition: 'border-color 0.3s ease'
                                                            }}
                                                            value={formData[`lastName_child_${itemIndex}_${index}`] || ''}
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>

                            <div className="col-lg-4 theiaStickySidebar">
                                <div className="card order-details" style={{
                                    borderRadius: '15px',
                                    border: 'none',
                                    boxShadow: '0 0 20px rgba(0,0,0,0.05)',
                                    overflow: 'hidden'
                                }}>
                                    <div className="card-body" style={{ padding: '0' }}>
                                        <div style={{ padding: '20px' }}>
                                                                        {cartItems.map((item, index) => {
                                // Determine item type and render accordingly
                                const renderHotelItem = () => (
                                    <div key={index} className="border-bottom pb-3 mb-3">
                                        <div className="d-flex align-items-center mb-3">
                                            <img 
                                                src={item.hotel?.image || '/assets/img/hotels/hotel-01.jpg'} 
                                                alt={item.hotel?.name || 'Hotel'}
                                                style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    marginRight: '15px'
                                                }}
                                                onContextMenu={(e) => e.preventDefault()}
                                                onDragStart={(e) => e.preventDefault()}
                                            />
                                            <h6 style={{
                                                color: '#1e88e5',
                                                fontSize: '1.2rem',
                                                fontWeight: '500',
                                                margin: 0
                                            }}>{item.hotel?.name || 'Hotel'}</h6>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Room Type</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.room?.name} x{item.quantity}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Board</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.room?.rates?.[0]?.boardName}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Check-in</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {new Date(item.check_in).toLocaleDateString()}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Check-out</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {new Date(item.check_out).toLocaleDateString()}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Guests</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.room?.rates?.[0]?.adults} Adult{item.room?.rates?.[0]?.adults > 1 ? 's' : ''}
                                                {item.room?.rates?.[0]?.children > 0 && `, ${item.room?.rates?.[0]?.children} Child${item.room?.rates?.[0]?.children > 1 ? 'ren' : ''}`}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Nights</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.room?.rates?.[0]?.nights}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Price</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.price} {selectedCurrency}
                                                <small style={{ color: '#666', fontSize: '0.8rem', marginLeft: '5px' }}>
                                                    ({item.quantity} {item.quantity === 1 ? 'room' : 'rooms'})
                                                </small>
                                            </span>
                                        </div>

                                        {/* Özel Teklifler */}
                                        {item.room?.rates?.[0]?.offers && item.room.rates[0].offers.length > 0 && (
                                            <div className="mt-2">
                                                <span style={{ color: '#757575', fontSize: '0.9rem' }}>Special Offers</span>
                                                {item.room.rates[0].offers.map((offer, offerIndex) => (
                                                    <div key={offerIndex} className="d-flex justify-content-between align-items-center">
                                                        <span style={{ color: '#212121', fontSize: '0.9rem' }}>{offer.name}</span>
                                                        <span style={{ color: '#28a745', fontWeight: '500' }}>
                                                            {offer.amount} {item.room.rates[0].currency}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* İptal Politikaları */}
                                        {item.room?.rates?.[0]?.cancellationPolicies && item.room.rates[0].cancellationPolicies.length > 0 && (
                                                        <div className="mt-3">
                                                            <span style={{ color: '#757575', fontSize: '0.9rem', marginBottom: '10px', display: 'block' }}>Cancellation Policy</span>
                                                            {item.room.rates[0].cancellationPolicies.map((policy, policyIndex) => {
                                                                const policyDate = new Date(policy.from);
                                                                const checkInDate = new Date(item.check_in);
                                                                
                                                                return (
                                                                    <div key={policyIndex} className="cancellation-policy-container">
                                                                        {/* Free Cancellation Badge */}
                                                                        <div className="modern-badge" 
                                                                            style={{
                                                                                background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                                                                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)',
                                                                                display: 'flex',
                                                                                alignItems: 'flex-start',
                                                                                padding: '1rem',
                                                                                borderRadius: '12px',
                                                                                marginBottom: '10px'
                                                                            }}>
                                                                            <div style={{ 
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                width: '32px',
                                                                                height: '32px',
                                                                                borderRadius: '8px',
                                                                                marginRight: '1rem',
                                                                                background: '#4CAF50'
                                                                            }}>
                                                                                <i className="isax isax-tick-circle" style={{ color: 'white', fontSize: '18px' }}></i>
                                                                            </div>
                                                                            <div>
                                                                                <h6 style={{ 
                                                                                    color: '#2E7D32',
                                                                                    fontSize: '0.9rem',
                                                                                    fontWeight: '600',
                                                                                    marginBottom: '5px'
                                                                                }}>
                                                                                    Free Cancellation Period
                                                                                </h6>
                                                                                <p style={{ 
                                                                                    color: '#1B5E20',
                                                                                    fontSize: '0.8rem',
                                                                                    margin: 0
                                                                                }}>
                                                                                    Cancel without any charges until {new Date(policy.from).toLocaleDateString()}
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {/* Partial Charge Badge */}
                                                                        <div className="modern-badge"
                                                                            style={{
                                                                                background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                                                                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.15)',
                                                                                display: 'flex',
                                                                                alignItems: 'flex-start',
                                                                                padding: '1rem',
                                                                                borderRadius: '12px',
                                                                                marginBottom: '10px'
                                                                            }}>
                                                                            <div style={{ 
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                width: '32px',
                                                                                height: '32px',
                                                                                borderRadius: '8px',
                                                                                marginRight: '1rem',
                                                                                background: '#FF9800'
                                                                            }}>
                                                                                <i className="isax isax-info-circle" style={{ color: 'white', fontSize: '18px' }}></i>
                                                                            </div>
                                                                            <div>
                                                                                <h6 style={{ 
                                                                                    color: '#E65100',
                                                                                    fontSize: '0.9rem',
                                                                                    fontWeight: '600',
                                                                                    marginBottom: '5px'
                                                                                }}>
                                                                                    Cancellation Fee Period
                                                                                </h6>
                                                                                <p style={{ 
                                                                                    color: '#F57C00',
                                                                                    fontSize: '0.8rem',
                                                                                    margin: 0
                                                                                }}>
                                                                                    From {new Date(policy.from).toLocaleDateString()} until {new Date(item.check_in).toLocaleDateString()}<br/>
                                                                                    <span style={{ fontWeight: '600' }}>
                                                                                        Fee: {cancellationPrices[item.id]?.[policy.from] || policy.amount} {selectedCurrency}
                                                                                    </span>
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {/* Non-Refundable Badge */}
                                                                        <div className="modern-badge"
                                                                            style={{
                                                                                background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                                                                                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.15)',
                                                                                display: 'flex',
                                                                                alignItems: 'flex-start',
                                                                                padding: '1rem',
                                                                                borderRadius: '12px'
                                                                            }}>
                                                                            <div style={{ 
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                width: '32px',
                                                                                height: '32px',
                                                                                borderRadius: '8px',
                                                                                marginRight: '1rem',
                                                                                background: '#F44336'
                                                                            }}>
                                                                                <i className="isax isax-danger" style={{ color: 'white', fontSize: '18px' }}></i>
                                                                            </div>
                                                                            <div>
                                                                                <h6 style={{ 
                                                                                    color: '#D32F2F',
                                                                                    fontSize: '0.9rem',
                                                                                    fontWeight: '600',
                                                                                    marginBottom: '5px'
                                                                                }}>
                                                                                    Non-Refundable Period
                                                                                </h6>
                                                                                <p style={{ 
                                                                                    color: '#B71C1C',
                                                                                    fontSize: '0.8rem',
                                                                                    margin: 0
                                                                                }}>
                                                                                    After {new Date(item.check_in).toLocaleDateString()}<br/>
                                                                                    <span style={{ fontWeight: '600' }}>
                                                                                        Full amount will be charged
                                                                                    </span>
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                    </div>
                                );

                                const renderTourItem = () => (
                                    <div key={index} className="border-bottom pb-3 mb-3">
                                        <div className="d-flex align-items-center mb-3">
                                            <img 
                                                src={item.tour?.thumbnail || '/assets/img/tours/tour-01.jpg'} 
                                                alt={item.tour?.name || 'Tour'}
                                                style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    marginRight: '15px'
                                                }}
                                                onContextMenu={(e) => e.preventDefault()}
                                                onDragStart={(e) => e.preventDefault()}
                                            />
                                            <h6 style={{
                                                color: 'rgba(210, 110, 50, 0.95)',
                                                fontSize: '1.2rem',
                                                fontWeight: '500',
                                                margin: 0
                                            }}>{item.tour?.name || 'Tour'}</h6>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Tour Date</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {new Date(item.check_in).toLocaleDateString()}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Participants</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.booking_details?.adults} Adult{item.booking_details?.adults > 1 ? 's' : ''}
                                                {item.booking_details?.children > 0 && `, ${item.booking_details?.children} Child${item.booking_details?.children > 1 ? 'ren' : ''}`}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Price</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.price} {selectedCurrency}
                                            </span>
                                        </div>
                                    </div>
                                );

                                const renderTransferItem = () => (
                                    <div key={index} className="border-bottom pb-3 mb-3">
                                        <div className="d-flex align-items-center mb-3">
                                            <img 
                                                src='/assets/img/cars/car-01.jpg'
                                                alt={`${item.transfer?.vehicle?.type} ${item.transfer?.vehicle?.category}`}
                                                style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    marginRight: '15px'
                                                }}
                                                onContextMenu={(e) => e.preventDefault()}
                                                onDragStart={(e) => e.preventDefault()}
                                            />
                                            <h6 style={{
                                                color: 'rgba(210, 110, 50, 0.95)',
                                                fontSize: '1.2rem',
                                                fontWeight: '500',
                                                margin: 0
                                            }}>{item.transfer?.vehicle?.type} {item.transfer?.vehicle?.category}</h6>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Route</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.transfer?.route?.from?.name} → {item.transfer?.route?.to?.name}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Transfer Date</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {new Date(item.departure_date).toLocaleDateString()}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Passengers</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.passengers?.adults} Adult{item.passengers?.adults > 1 ? 's' : ''}
                                                {item.passengers?.children > 0 && `, ${item.passengers?.children} Child${item.passengers?.children > 1 ? 'ren' : ''}`}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Price</span>
                                            <span style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.price} {selectedCurrency}
                                            </span>
                                        </div>
                                    </div>
                                );

                                // Return appropriate render based on item type
                                if (item.type === 'tour') {
                                    return renderTourItem();
                                } else if (item.type === 'transfer') {
                                    return renderTransferItem();
                                } else {
                                    return renderHotelItem();
                                }
                            })}

                            <div className="pt-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span style={{ color: '#1e88e5', fontSize: '1.1rem', fontWeight: '500' }}>
                                        Total Amount
                                    </span>
                                    <span style={{ color: '#1e88e5', fontSize: '1.3rem', fontWeight: '600' }}>
                                        {typeof totalPrice === 'number' ? totalPrice.toFixed(2) : '0.00'} {selectedCurrency}
                                    </span>
                                </div>
                            </div>

                                            <div className="mt-4">
                                                <button 
                                                    type="submit"
                                                    className="btn btn-primary w-100"
                                                    style={{
                                                        padding: '12px 24px',
                                                        borderRadius: '12px',
                                                        fontSize: '1.1rem',
                                                        fontWeight: '500',
                                                        background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                                                        border: 'none',
                                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    disabled={buttonLoading}
                                                >
                                                    {buttonLoading ? (
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                            Processing...
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <i className="isax isax-tick-circle me-2"></i>
                                                            Confirm Booking
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    )
}
